
// ── Resolution intents ───────────────────────────────────────────────────────────
//
// An **intent** is a client→authority *request the authority resolves* — NOT a
// reducer command. The client can claim "I
// want to attack"; only the authority runs `resolve` (in `@lancer-vtt/rules`),
// draws the dice, and mints the resolved `apply_resolved` command. That split IS
// the anti-cheat boundary — a client can never mint a resolved outcome.
//
// **Home of the intent type, decided deliberately:** the intent is **wire-protocol
// data**, exactly like `Command` — a client serializes it and sends it. It carries
// only primitives (combatant/weapon ids, an accuracy int), so it has no dependency
// on `@lancer-vtt/domain` and lives here in `shared/` next to the other wire shapes.
// The resolver (`@lancer-vtt/rules`) imports `RequestAttack` from here to consume it;
// `shared/` stays leaf (it can't import `domain`/`rules`). This mirrors how the
// resolved *command* it produces is owned by `domain` while its *wire envelope*
// (`encounter_command`) is owned here.

/**
 * A **self-describing grid cell on the wire** (area-effects v1) — the structural twin of
 * `@lancer-vtt/domain`'s tagged `Cell` (`HexCell | SquareCell`), declared here because
 * `shared/` is leaf and cannot import `domain`. It carries the same `grid`-tagged JSON, so
 * a domain `Cell` assigns to it and back across the wire seam. An area resolution intent
 * rides a `WireCell` template origin; the resolver (`@lancer-vtt/rules`) reads it as a
 * `Cell` and validates it against the encounter's grid (warn-and-allow on geometry).
 */
export type WireCell =
  | { grid: "hex"; q: number; r: number; s: number }
  | { grid: "square"; x: number; y: number };

/**
 * **The attack intent.** A client→authority request to resolve a
 * weapon attack of `attacker` against `target`. `accuracy` is the *net*
 * accuracy/difficulty the client declares (positive = accuracy, negative =
 * difficulty); the authority's `resolve` rolls the extra d6 and records the result.
 * `weapon` is an optional breadcrumb to the weapon used (the resolver reads that copy's
 * typed damage profile). The reducer never sees this — only the resolved steps do.
 */
/**
 * **What a weapon attack is aimed at** (area-effects v1) — a discriminated union, because an
 * attack now targets either a **unit** (the classic single-target click — `{ kind: "unit" }`)
 * or a bare **cell** (`{ kind: "cell" }`, the AoE origin a Blast/Line/Cone/Burst weapon needs).
 * The resolver derives the *affected* combatants from the weapon's frozen template around this
 * aim (`attackAffectedCombatants`): a unit target resolves against that one combatant; a cell
 * target fans out to everyone the template catches. Line/Cone read the cell as the `toward`
 * point and the attacker's anchor as the apex.
 */
export type AttackTarget = { kind: "unit"; id: string } | { kind: "cell"; cell: WireCell };

export type RequestAttack = {
  type: "request_attack";
  attacker: string;
  target: AttackTarget;
  /**
   * **More targets for a weapon that attacks several at a time** ("This weapon can attack two
   * targets at a time": the Sharanga Missiles, the Tiger-Hunter Combat Sheathe), beside the unit
   * `target`. The weapon's own entry caps how many (`attacksTargets`); each is range- and
   * LoS-gated like the first, and the attack fans out with one damage roll. Absent ⇒ one target.
   */
  alsoTargets?: string[];
  /**
   * Optional weapon **instance key** — which equipped copy to fire (`WeaponProfile.instanceId`,
   * falling back to the compendium `id` for fixtures; see `weaponInstanceKey`). This is *identity*,
   * NOT a compendium data key: two mounts of the same weapon type carry distinct instance keys, so
   * the resolver picks the right physical copy (A2 reads that copy's damage profile; A1 is
   * weapon-agnostic). A built-in melee-action attack (`act_ram`, …) passes the action id here
   * instead. Absent ⇒ the attacker's first frozen weapon.
   */
  weapon?: string;
  /**
   * Optional **firing-profile** selection for a multi-profile weapon: names one of the
   * weapon's alternate `profiles` (e.g. Spin-Up). Absent = the weapon's default profile.
   * The recorded-choice half of profile selection; the state-lookup half (Apocalypse Die)
   * is the atom interpreter's. Orthogonal to `weapon`:
   * `weapon` picks the physical copy, `profile` its firing MODE (alt profiles share one instance key).
   */
  profile?: string;
  /**
   * Which way to use a weapon that is **either ranged or melee, not both in the same turn** (the
   * Ram Cannon, `WeaponProfile.eitherMode`). Absent ⇒ the default: melee when the target is
   * within the weapon's Threat, ranged beyond it. The room rejects `mode` on any other weapon,
   * `"melee"` beyond Threat, and a second mode in the same turn. Orthogonal to {@link profile}:
   * the Ram Cannon has one damage profile in both modes, and the mode changes only whether the
   * attack is ranged (Engaged, cover, `match.ranged`).
   */
  mode?: "melee" | "ranged";
  /** Net accuracy/difficulty declared by the client (+acc / −diff / 0). */
  accuracy: number;
  /**
   * The recorded **aim direction** cell for a **directional** area weapon (LINE/CONE).
   * Line/Cone radiate from the *attacker's* own space (the apex),
   * so beyond the origin they need a point that fixes which way the template points; this
   * is that point. A {@link WireCell}, read by the resolver as the `toward` of the
   * grid-agnostic `linePattern`/`conePattern`. Absent for Blast/Burst and single-target
   * attacks (radial / point templates need no direction). This is an **input** field — it
   * does not change the to_hit/damage step shape, only which combatants the fan-out catches.
   */
  toward?: WireCell;
  /**
   * The recorded **reaction-time direction** answer — a spatial `needs_decision`
   * round-trip, distinct from {@link toward} above. `toward` is a
   * PRE-declared aim direction submitted up front (the client picks it before this intent is
   * ever sent); this field is the answer to a decision the resolver raised MID-resolution (Ace's
   * Acrobatics: a triggered on-miss reaction move with no direction known until the miss
   * happens — `t_ace:1`, RAW's own name for the rank and its reaction). Kept as its own
   * field rather than reusing `toward` because a directional Line/Cone
   * attack could in principle ALSO trigger an on-miss reaction needing its own direction — the
   * two would collide on one slot. Absent on a first submit (nothing recorded yet); the answered
   * re-submit (echoing the `needs_decision`'s `intent` back, per {@link PromptRequest}) fills it
   * in, and the resolver reads it into `EffectContext.toward` for the re-run (see
   * the on_miss fire site — `packages/rules/src/encounter/resolve/attackTriggerFires.ts`'s
   * `fireMissTriggers`). The CLI's `--react-toward` is the scriptable source.
   */
  reactionToward?: WireCell;
  /**
   * Placement cells for an attack-fired **area-scoped `deploy_object`** (Earthshaker Shells' two
   * hard-cover segments; `npc-features-fielded-roster` Phase 18), in section order — the attack
   * twin of {@link RequestActivate.sites}. Each must be a free space inside the atom's `within`
   * area; a missing or illegal one raises the per-section options prompt instead
   * (`deployObjectSitePromptId`, answered in `choices`). Absent for every other attack.
   */
  sites?: WireCell[];
  /**
   * Recorded **choices** for a `choose` atom an on-hit/on-crit effect offers
   * — keyed by the `choose` atom's `prompt` id, valued by the
   * selected option id. The recorded-decision half of the `choose` roundtrip: a player
   * (or a scripted fixture — Custom Paint Job) supplies which branch to take, recorded
   * into the intent so the authority's interpreter resolves it deterministically and
   * replay reproduces it. Absent / an unanswered prompt → the interpreter takes the
   * first option (the faithful no-roundtrip default). Post-v1 a remote-human prompt
   * fills this over a `needs_decision` roundtrip; the *shape* is identical.
   */
  choices?: Record<string, string>;
  /**
   * Recorded **dice** — the monotonic input log replayed on a prompt re-resolve
   * on a prompt re-resolve. The iterate-pure round-trip re-runs the whole resolution
   * from the top on each answer; recording only `choices` re-draws any dice rolled
   * *before* the prompt, landing different numbers (the Lock On consume/Brace defect).
   * On a `needs_decision` the authority rides the dice-so-far back here; the answered
   * re-submit feeds them to the authority's {@link RecordingDiceSource}, which replays
   * them so the resolution reproduces the exact numbers it paused on. Absent on a first
   * submit (nothing recorded yet) → all dice drawn fresh. Sibling of {@link choices}.
   */
  recordedDice?: number[];
  /**
   * **Overwatch reaction.** When `true`, this attack
   * is fired as a **reaction** rather than a standard/quick action: the authority gates it
   * on the attacker having a **reaction left this round** (not on whose turn it is — a
   * reaction is spent *out of turn*) and on the target being within the attacker's
   * **threat range** (footprint-to-footprint `@lancer-vtt/space` distance ≤ the threat
   * threshold; default melee threat 1). It spends the per-round reaction, not an action
   * point. Absent / `false` ⇒ an ordinary action (the v1 behavior). Overwatch is the
   * canonical trigger — an enemy acting in threat range — surfaced here as the reactor's
   * declared reaction-attack (the move-trigger detection is the map's; the resolution is
   * this intent).
   */
  reaction?: boolean;
  /**
   * **Tech attack.** When `true`, this resolves as a *tech attack*
   * rather than a weapon (ranged/melee) attack: `d20 + TECH ATTACK ± accuracy` vs the
   * target's **E-DEFENSE** (not Evasion), ignoring cover (the resolver records `vs:
   * "edef"`). The canonical tech attack is **INVADE** — a `request_attack { tech: true,
   * weapon: <the INVADE system id> }` whose system carries the on-hit invasion menu (2
   * heat + FRAGMENT SIGNAL → Impaired & Slowed) as an overlay `triggered`/`choose`
   * effect. LOCK ON and SCAN are *not* tech attacks (no roll) — they ride
   * {@link RequestActivate}. Absent / `false` ⇒ an ordinary weapon attack (vs Evasion).
   */
  tech?: boolean;
  /**
   * **Release a held action** (RAW Core pp. 71-72's "as a reaction, you may … take the
   * readied action"). When present, this attack is resolving `releasedBy`'s ARMED HOLD out
   * of turn (`prepare`) rather than firing as an ordinary standard action: the authority
   * validates it against the held record (readied verb
   * + weapon match) and skips every gate `prepare` already charged — no action-point charge, no
   * once-per-turn re-stamp, no Ordnance re-check, no `reactionSlot`/`reactionUses` spend — while
   * still re-checking fire-time-variable legality (range/target/loaded) and clearing the hold on
   * a successful resolve. Structurally must equal `attacker` (a release always fires the
   * preparer's own shot). Absent ⇒ an ordinary attack, unchanged (the v1 behavior). Rides the
   * same `request_attack` envelope (rather than a bespoke message) so the cross-actor Brace
   * prompt round-trip composes for free — the echoed re-submit carries this
   * field right along with it.
   */
  releasedBy?: string;
  /**
   * **The shared ×0.5 damage-scaling op** — Covering Fire's RAW "half damage, Heat, or
   * Burn" (`t_heavy_gunner:1`). When `"half"`, the resolver
   * halves this attack's final damage total AND its Heat delta AND its Burn delta —
   * each quantity **independently** rounded **up** (Golden Rule II, rulebook p.11) —
   * applied AFTER crit / Reliable / Overkill contributions settle each instance's
   * final, mitigated amount (beside the existing resistance halving precedent, `mitigate` in
   * `resolve/damage.ts`). This is NOT a folded standing modifier gated on a status — it's a
   * scoped field on the request itself, set by whoever **grants** the scaled attack
   * (Covering Fire's reaction-offer "take" path; later, `t_executioner:1`'s Backswing
   * Cut inherits the same field at its own grant site). **Server-stamped only**: the
   * room gate drops a client-supplied value on a plain (non-granted) attack — a
   * forging client can't gain anything (a fake `"half"` only nerfs its own damage),
   * but a client OMITTING the field on a granted reaction shot would silently
   * un-halve it, so the server sets this, never trusts it from the wire. Absent ⇒ an
   * ordinary, unscaled attack (the v1 behavior).
   */
  damageScale?: "half";
  /**
   * **The follow-up reservation consume signal** — Executioner's Backswing Cut
   * cash-in. When set, this
   * `request_attack` is the free half-damage follow-up authorized by a live reservation status
   * of THIS name on the attacker: the room's `stampBackswingFollowup` derivation matched it
   * (same weapon instance ∧ target ≠ the reservation's excluded partner ∧ the attacker's acting
   * turn) and stamped both this field and `damageScale:"half"`. Downstream it is the ONE signal
   * that (a) zeroes the action-point cost so the budget/action-use gates pass (a free action),
   * and (b) folds a `remove_status <name>` sibling into the resolution's own outcome batch so the
   * reservation is consumed and undoes as one unit with the attack.
   * **Server-stamped only**, exactly like {@link damageScale}: the room derives it from live
   * reservation state and drops any client-supplied value on a plain attack — a forging client
   * can never mint a free attack, since the stamp only lands on a genuine structural match. Absent
   * ⇒ an ordinary attack that spends normally.
   */
  consumeReservation?: string;
}

/**
 * **The action-activation intent** — the `action` kind of the three-kind effect
 * taxonomy. A client→authority request to *activate* an overlay
 * `action` effect a combatant owns (an item/system/talent action with its own
 * intent — Overpower Caliber's reaction, a charge-up). Unlike `RequestAttack` (which
 * resolves the Lancer attack spine), this resolves the action's authored `effect`
 * atom list through the interpreter: the same `force_save`/`add_status`/`resource`/
 * `if`/`choose` path, with no to-hit/damage of its own. The authority gathers the
 * matching `action` off the actor's overlay (keyed by `action`), interprets it, and
 * records the emitted grounded steps.
 *
 * The activation **cost** (Quick/Full/Reaction/…) is priced off the overlay `activation`
 * (`intentCost` in `@lancer-vtt/rules`) and gated by the room's action-economy budget.
 * `target` binds the action's `target` actor ref when the action targets another
 * combatant (Overpower Caliber's extra strike); absent → a self-action.
 */
export type RequestActivate = {
  type: "request_activate";
  /** The combatant activating the action (the `self`/owner of the effect). */
  actor: string;
  /** The overlay entity id whose `action` effect to activate (an item/system id). */
  action: string;
  /** Optional target combatant the action binds its `target` actor ref to. */
  target?: string;
  /**
   * The recorded **template-center** cell for an **area** action (area-effects v1, Q3) —
   * the origin a `for_each` selector with `origin: "site"` enumerates from (HEX/Smoke
   * Charges thrown to a point). A {@link WireCell} (the tagged position), validated against
   * the encounter's grid by the authority; range/LoS validity is **warn-and-allow** (GM
   * fiat). Absent for a non-area action. This is the field `in_area`'s deferral named as
   * missing — the intent now carries the template origin.
   */
  site?: WireCell;
  /**
   * The recorded **direction** cell for a line/cone area action (area-effects, phase-2 —
   * carried on the intent now so the wire shape is stable; v1 enumerates disk-only).
   */
  toward?: WireCell;
  /**
   * The player-picked cells for a **directed multi-pick placement** action (`jericho-directed-
   * placement` D2/D8 — `deploy_object`'s sections, e.g. Jericho's two cover sections or a
   * turret drone's single spawn cell). The new precedent for an array-of-cells command:
   * deliberately **not** an overload of {@link site}/{@link toward} — `toward` means a line/cone
   * aim direction and `site` is validated by `checkActivateSiteRange`, so folding placement
   * picks into either would make the wire lie about what it carries. Ordered — pick order is
   * meaningful (`checkDirectedDeployPlacement` walks it in sequence, each cell adjacent to the
   * deployer and to the previous pick). Each is a {@link WireCell}; a per-cell grid mismatch is
   * **warn-and-allow** (GM fiat), the same posture `site`/`toward` already take. Absent for a
   * non-placement action. Unconsumed as of this field's introduction — `interpretDeployObject`
   * starts reading it in a later phase.
   */
  sites?: WireCell[];
  /** Recorded choices for a `choose` atom in the action's effect, keyed by prompt id. */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
  /**
   * **Release a held granted-action.** Mirrors
   * {@link RequestAttack.releasedBy} exactly: when present, this activation is resolving
   * `releasedBy`'s ARMED HOLD out of turn rather than firing as an ordinary activation — the
   * authority validates it against the held record (`intent.action` must equal
   * `held.readiedActionId`) and skips every gate `prepare` already charged (the Quick action
   * point, the once-per-turn ledger stamp, `checkGrantedActionLegality`'s Protocol/round/scene
   * gate — all checked at `prepare`), while still consuming the round/scene usage charge NOW,
   * at release (the consumed-count ledger, extending Loading's
   * gated-at-prepare/consumed-at-release pattern to a granted action's own Limited/1-scene
   * charge — an untriggered lapse never decrements it). Structurally must equal `actor`.
   * Absent ⇒ an ordinary activation, unchanged.
   */
  releasedBy?: string;
}

/**
 * **The GM override wrapper** (`decisions/0209`, the `decisions/0042` amendment). The GM wraps a
 * gameplay intent to take it past every rule check: the room runs a structural check only (the
 * actor, target and weapon exist and are on the map; the carrier owns the weapon) and then the
 * SAME resolution the gameplay intent gets, with the forced cost policy (spend on the unit's own
 * turn, floored at 0; no action spend off-turn; no attack lock). The wrapped intent carries no
 * flag: the gameplay entry point has no skip parameter, and a forced resolution is marked
 * `forced` on its logged `apply_resolved` and on `ResolutionAbout`.
 *
 * **GM-only** at the permission gate (`canResolveIntent`). Only `request_attack` is forceable so
 * far; any other wrapped intent is rejected as not forceable yet. A prompt raised by a forced
 * resolution echoes the WRAPPER, so its answer re-submits through the same door; answer sites
 * reach the wrapped intent through {@link mapForcedIntent}.
 */
export type GmForce = {
  type: "gm_force";
  intent: Exclude<EncounterIntent, { type: "gm_force" }>;
};

/** The intent a {@link GmForce} wraps, or the intent itself. */
export function unwrapForced(intent: EncounterIntent): Exclude<EncounterIntent, { type: "gm_force" }> {
  return intent.type === "gm_force" ? intent.intent : intent;
}

/**
 * Rewrite the gameplay intent inside a {@link GmForce} (or `intent` itself when unwrapped),
 * keeping the wrapper. Every prompt-answer site goes through this, so recording a choice or a
 * direction on a forced resolution's echo re-submits it forced.
 */
export function mapForcedIntent(intent: EncounterIntent, f: (inner: Exclude<EncounterIntent, { type: "gm_force" }>) => Exclude<EncounterIntent, { type: "gm_force" }>): EncounterIntent {
  return intent.type === "gm_force" ? { ...intent, intent: f(intent.intent) } : f(intent);
}

/**
 * **The Barrage intent** — **ONE shot** of a
 * sequential, roll-as-you-assign Barrage: a single weapon firing at a single target, resolved and
 * committed **immediately** (its own to-hit + damage roll, exactly like a {@link RequestAttack}).
 * A Barrage is **two weapons, or one SUPERHEAVY weapon** (Core Book p.70/71), but — unlike the
 * retired atomic bundle — the player commits ONE weapon at a time and may stop after the first.
 *
 * The 1–2 shots are held together as one logical Full action by the **mid-attack lock** (a synced
 * state on the acting combatant — `AttackLock`, not Barrage-specific), NOT by concatenating
 * sub-attacks. The authority disambiguates **opener vs continuation from that lock** (no flag,
 * per the two-command-tiers doctrine):
 *   - **Opener** (not mid-attack): charge the Full action, resolve, open the lock recording this
 *     weapon (auto-close immediately if no legal second weapon — a Superheavy or a one-weapon mech).
 *   - **Continuation** (mid-attack): per-step legality (not-already-fired / not-Superheavy /
 *     <2 fired), resolve, **no second charge**; auto-close the lock when no legal attack remains.
 *
 * Each shot is an independent `apply_resolved` → its own to-hit/damage/self-heat + AoE fan-out +
 * on-hit triggers + Brace interception (it IS a normal attack), and its own undo entry; undo walks
 * a two-weapon Barrage back shot-by-shot (two steps). The explicit abandon-after-one path is the
 * sibling {@link EndAttack}. Heat folds for free — shot 2 resolves against shot 1's committed state.
 */
export type RequestBarrage = {
  type: "request_barrage";
  /** The combatant making the Barrage (the wielder of this shot's weapon). */
  attacker: string;
  /** The frozen weapon **instance key** this shot fires (required — `weaponInstanceKey`: `instanceId`, else compendium `id`). Two mounts of the same weapon type have distinct keys, so a Barrage fires each independently and the lock tracks them apart. */
  weapon: string;
  /** Optional firing-profile selection for a multi-profile weapon (mirrors {@link RequestAttack.profile}). */
  profile?: string;
  /**
   * The **committed mount keys** (`decisions/0049`, 2026-09-24 amendment): `mount:<i>` for
   * `base.mounts[i]`, or a weapon's own instance key when it sits on no frozen mount (a
   * frame-integrated weapon, or any weapon of a combatant with no frozen mounts). Named on the
   * **opener**: one or two distinct mounts, or a Superheavy's mount alone; `weapon` must sit on
   * one of them, and each must hold a weapon that can fire now. Every later shot must fire a
   * weapon on these mounts. **Required on the opener** whenever the attacker has a weapon (the
   * room rejects an opener without it; a weaponless Barrage's fallback strike has no mount). On a
   * continuation it may be repeated, and must then equal the lock's set.
   */
  mounts?: string[];
  /** The unit or cell this shot fires at. */
  target: AttackTarget;
  /**
   * More unit targets for this shot's weapon when it attacks several at a time (the Sharanga
   * Missiles, the Tiger-Hunter Combat Sheathe) — {@link RequestAttack.alsoTargets}, forwarded onto
   * the synthesized shot and gated the same way. Absent ⇒ one target.
   */
  alsoTargets?: string[];
  /**
   * The aim direction for a directional Line/Cone shot — {@link RequestAttack.toward}, forwarded
   * onto the synthesized shot. A Superheavy weapon fires only through a Barrage (Core p.70), so a
   * Superheavy Line/Cone needs its aim here.
   */
  toward?: WireCell;
  /**
   * Placement cells for an attack-fired area `deploy_object` — {@link RequestAttack.sites},
   * forwarded onto the synthesized shot (Earthshaker Shells rides the Bombard's Superheavy
   * cannon, which fires only through a Barrage).
   */
  sites?: WireCell[];
  /** Net accuracy/difficulty for this shot (+acc / −diff / 0). */
  accuracy: number;
  /** Recorded `choose`-atom decisions for this shot (mirrors {@link RequestAttack.choices}). */
  choices?: Record<string, string>;
  /**
   * The recorded **reaction-time direction** answer for this shot (mirrors {@link
   * RequestAttack.reactionToward}).
   *
   * A Barrage shot is a full attack, so its DECLARED phase fires `on_attack_declared` clauses
   * exactly like a Skirmish does — including `move_self_toward_target` clauses that halt for a
   * magnitude (`upTo`) or direction (`toward: "chosen"`) pick: `t_hunter:1`'s Lunge (an
   * Auxiliary melee weapon in a Barrage is an utterly ordinary table action) and
   * `t_siege_specialist:2`'s Impact. Without this field those halts were **unanswerable** — the
   * server echoes the ORIGINAL wire intent for the client to re-submit, and a `RequestBarrage`
   * had nowhere to put the picked cell, so the prompt re-raised forever and the encounter hung
   * mid-combat. Absent on a first submit; the answered re-submit fills it in, exactly like the
   * {@link RequestAttack} twin, and `refereeIntent` forwards it onto the synthesized shot.
   */
  reactionToward?: WireCell;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **End the in-progress attack** (generalized past Barrage-only to the shared attack
 * lock) — the explicit
 * abandon-after-one path. A two-weapon mech that fired only its first weapon (or a Skirmish whose
 * primary opened the lock for an eligible Aux rider) stays mid-attack (the lock holds, blocking its
 * Overcharge/free actions — but NOT its standard move, which is deliberately allowed
 * mid-lock) until it either fires the next shot (auto-close) or sends this. The
 * authority verifies the actor is mid-attack, then closes the lock (`[close_attack]`) — no
 * resolution, no further charge (the action was charged on the opener). A one-weapon / Superheavy
 * Barrage, or a Skirmish with no eligible rider, auto-closes (or never opens) on its own, so this is
 * never needed for it.
 */
export type EndAttack = {
  type: "end_attack";
  /** The combatant whose in-progress attack to end (the lock's owner / acting actor). */
  attacker: string;
}

/**
 * **Stop a Barrage and keep it as a Skirmish** (`decisions/0049`, 2026-09-24 amendment: the
 * "Stop Firing, converts to Skirmish" exit). Legal only while `attacker` holds a Barrage lock
 * whose fired weapons all sit on ONE mount (a primary, optionally with its Auxiliary rider — a
 * legal Skirmish), none a Superheavy, and Skirmish is still usable this turn. The authority
 * re-prices the action — the Full spend becomes a Quick (one action point back), Barrage is
 * un-marked and Skirmish marked in the per-turn ledger — and closes the lock, as one undoable
 * command. No resolution, no dice. Once weapons on BOTH mounts have fired, the exit is
 * {@link EndAttack} instead.
 */
export type StopBarrageAsSkirmish = {
  type: "stop_barrage_as_skirmish";
  /** The combatant whose Barrage converts (the lock's owner / acting actor). */
  attacker: string;
}

/**
 * **The Stabilize primary choice** — RAW (Core Book p.70) a
 * PC Stabilize picks **one** of these self-maintenance effects:
 *   - `"cool"` — clear ALL heat and remove the **Exposed** status (reuses the shipped
 *     heat track + Exposed model).
 *   - `"patch"` — spend **one repair** from the mech's Repair Cap to restore **all** HP
 *     (an *in-combat* consumer of the SHIPPED `repair_cap` resource). Hard-rejected
 *     when no repairs remain.
 */
export type StabilizePrimary = "cool" | "patch";

/**
 * **The Stabilize secondary choice** — RAW a PC Stabilize then
 * picks **one** of these:
 *   - `"reload"` — reload all **Loading** weapons. *No Loading-weapon state exists in the
 *     engine yet*, so this is a documented **no-op stub** (surfaces a warning, emits no
 *     step) until a weapon-state model lands.
 *   - `"clear_burn"` — clear all **Burn** currently affecting you (reuses the shipped Burn model).
 *   - `"clear_condition"` — clear **one** listed condition affecting you (Immobilized /
 *     Impaired / Jammed / Lock On / Shredded / Slowed / Stunned); the name rides `secondaryStatus`.
 *   - `"end_save_effect"` — end one ongoing harmful effect on you that a save would end;
 *     the status name rides `secondaryStatus`.
 */
export type StabilizeSecondary = "reload" | "clear_burn" | "clear_condition" | "end_save_effect";

/**
 * **The Stabilize intent** — the mech self-maintenance **Full
 * action**. RAW (PC version, Core Book p.70): choose one `primary` (Cool | Patch), then
 * choose one `secondary`. BOTH choices are collected **up front** (one round-trip — the
 * SkirmishChooser/BarrageChooser pattern) since both menus are known at action time; the
 * authority resolves them into ONE `apply_resolved` (`kind: "stabilize"`) charged as a
 * single Full action and undoable as one logical unit.
 *
 * The shared resolution primitive (`stabilizeSteps`) is **choice-driven**, so the NPC
 * variant (clear heat + Exposed + reload Loading only — no HP heal, no condition clear)
 * selects a SUBSET (`primary: "cool", secondary: "reload"`) without the PC menu being
 * baked into the resolution path.
 */
export type RequestStabilize = {
  type: "request_stabilize";
  /** The combatant taking Stabilize (the self-maintenance actor — Stabilize only ever targets self). */
  actor: string;
  /** The chosen primary effect (Cool | Patch). */
  primary: StabilizePrimary;
  /** The chosen secondary effect. Optional — a degenerate/NPC call may omit it (primary only). */
  secondary?: StabilizeSecondary;
  /**
   * The specific status name for `clear_condition` / `end_save_effect` (the one condition /
   * save-endable effect to clear). Ignored for `cool`/`clear_burn`/`reload`.
   */
  secondaryStatus?: string;
}

/**
 * **The Overcharge intent** — the mech reactor push. RAW (Core Book
 * p.78): **once per turn**, a mech may make any **Quick action as a free action** (even one
 * it has already taken this turn) by paying escalating self-heat — **1 → 1d3 → 1d6 → 1d6+4**,
 * advancing each use and resetting on a **full repair**. Mech-only (NPCs cannot Overcharge,
 * Core p.280). The authority rolls the heat at the actor's current escalation step (drawn +
 * recorded), runs it through the existing heat / Overheating path (an Overcharge that
 * crosses Heat Cap into Overheating is intended), advances the step, and grants **+1 Quick
 * action** to the actor's turn budget — all folded into ONE atomic, undoable unit.
 *
 * Self-target only (you push your own reactor); no choices, so a single round-trip with no
 * prompt. The once-per-turn gate + the mech-only / on-turn walls are enforced authority-side
 * before any dice (`checkOverchargeLegality`).
 */
export type RequestOvercharge = {
  type: "request_overcharge";
  /** The mech taking Overcharge (self only — it pushes its own reactor). */
  actor: string;
  /** Recorded answers: Reactor Stabilizer's keep-or-reroll on the Overheating roll (`reroll:<actor>:overheating`). */
  choices?: Record<string, string>;
  /** The dice drawn before a halt, echoed back on the answered re-submit (see `RequestAttack.recordedDice`). */
  recordedDice?: number[];
}

/**
 * **The Full Tech intent** — **ONE**
 * Quick Tech option fired at a single target, resolved and committed **immediately**, the unit a
 * Full Tech's two picks are built from. Core Book p.71: "choose two QUICK TECH options... you can
 * choose the same option multiple times" — nothing in RAW mandates the two resolve together, so
 * (like Barrage's two shots) each pick resolves on its own, held together as one logical Full
 * action by the **mid-attack lock** (`AttackLock`, `action: "full_tech"` — the third consumer
 * after Barrage/Skirmish).
 *
 * The authority disambiguates **opener vs continuation from that lock** (no flag):
 *   - **Opener** (not mid-attack): the option must be a known Quick Tech id; charge the Full
 *     action (once), resolve through the SAME `quickTechIntent` router a standalone Quick Tech
 *     uses, open the lock recording the fired option id.
 *   - **Continuation** (mid-attack): the option must be a known id. A mech **may repeat** it
 *     (Bolster+Bolster, RAW's "may repeat the same one"); an NPC may not (Core p.280: "two
 *     different Quick Tech actions"), so an NPC's repeat is refused. Resolve the same way, **no
 *     second charge**; auto-close the lock once 2 options have fired.
 *
 * Each pick is an independent `apply_resolved` (Invade → a tech attack's own to-hit/damage/heat;
 * Lock On/Bolster/Scan → a plain activate), and its own undo entry — undo walks a two-pick Full
 * Tech back pick-by-pick. The explicit abandon-after-one path is the shared {@link EndAttack}. The
 * per-sub tech-target walls (self-target, tech-immunity, willing-ally-Invade deferral, Sensors
 * range + LoS) are the SAME walls a standalone routed intent gets — reused, not duplicated,
 * because there is only ever one sub per intent now.
 *
 * **Why a distinct wire type, not the bare routed `request_attack`/`request_activate` under a
 * lock-continuation predicate**: a Full Tech opener is NOT
 * economy-identical to the same option taken as a standalone Quick Tech — it costs Full (charged
 * once, on the opener) and stamps the `"full_tech"` ledger, vs. Quick Tech's 1-point
 * `"quick_tech"` stamp, even though the two are otherwise wire-identical. Keeping
 * `request_full_tech` its own type makes the wire itself the unambiguous "this is a Full Tech
 * sub" signal, no flag needed.
 */
export type RequestFullTech = {
  type: "request_full_tech";
  /** The combatant making the Full Tech (the caster of this pick). */
  actor: string;
  /** The Quick Tech option id this pick takes (`scan` | `lock_on` | `invade` | `bolster` — a mech may repeat it across the two picks of one Full Tech, an NPC may not). */
  option: string;
  /** The combatant this pick targets (an enemy for Invade/Lock On/Scan, an ally for Bolster). */
  target: string;
  /** Net accuracy (+) / difficulty (−) for the tech-attack route (Invade); ignored by activate routes. Default 0. */
  accuracy?: number;
  /** Recorded `choose`-atom decisions for this pick (mirrors {@link RequestAttack.choices}). */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **The Hide intent** — the mech stealth **Quick action**
 * (Core Book p.74). The actor becomes **Hidden** (a real targeting-gating status — a Hidden
 * unit can't be the direct target of attacks/hostile actions, only area effects), **provided
 * it has cover from / is unseen by every hostile**. The authority auto-computes that cover
 * precondition off the live map (`checkHideCover` via the shipped LoS/cover geometry): if any
 * hostile has a fully clear line (`visible && cover === "none"`) the unit is in the open and
 * Hide is **hard-rejected** before it applies — the rules-following tier. Self-target
 * only (you Hide yourself); no choices, one round-trip.
 *
 * The GM overrides the cover check by wrapping this intent in {@link GmForce} (`decisions/0209`),
 * NEVER with a `force` flag on this one.
 */
export type RequestHide = {
  type: "request_hide";
  /** The mech taking Hide (self only — you Hide yourself). */
  actor: string;
}

/**
 * **The Search intent** — the mech
 * stealth-countering **Quick action** (Core Book p.74): reveal a Hidden target via the RAW
 * opposed contest `1d20 + searcher.systems` vs `1d20 + target.agility`, **initiator (searcher)
 * wins ties**. Gated (before any dice) by budget → the searcher's **Sensors range**
 * (`checkTechRange`) → **line of sight** (`terrainVisibility`, the same `los.ts` geometry Hide
 * reuses). Non-interactive — the authority draws both d20s itself, no `needs_decision`
 * round-trip. Both rolls are always recorded (the `ContestStep`, draw-and-record) even on
 * a loss; on a searcher win **or tie**, the resolution additionally clears the target's Hidden
 * status. Searching a non-Hidden target is legal and simply reveals nothing (idempotent) — the
 * Hidden filter is a UI affordance, never an engine-level gate.
 */
export type RequestSearch = {
  type: "request_search";
  /** The searcher — the mech spending the Quick action (the contest's initiator/challenger). */
  actor: string;
  /** The target being Searched (the contest's defender). */
  target: string;
}

/**
 * **The Grapple-break intent** — the defender's
 * **Quick action** to break free of a `Grappled` bind (Core Book p.72): the room reads `actor`'s
 * `Grappled.partner` off its own statuses to find the other party, then resolves
 * `1d20 + actor.hull` vs `1d20 + partner.hull` via `resolveContest` (initiator/**breaker wins
 * ties**). On a breaker win-or-tie, clears the `Grappled` marker on both; on
 * a loss the grapple persists — the `ContestStep` is always recorded (draw-and-record).
 */
export type RequestGrappleBreak = {
  type: "request_grapple_break";
  /** The Grappled combatant attempting to break free (the contest's challenger). */
  actor: string;
}

/**
 * **The same-Size Grapple tiebreak intent** (Core Book p.69, verbatim: *"If both parties are the same SIZE, either can
 * make contested HULL checks at the start of their turn: the winner counts as larger than the
 * loser until this contest is repeated."*) — a dispatched **free** action (0 points), legal only
 * on the dispatcher's own turn, before it has spent anything (the `checkOrdnanceWindow`-shaped
 * nothing-spent proxy), while it carries a `Grappled` marker with a resolvable partner of the
 * SAME raw `base.size` (an unequal pair already has a size-derived dominant — nothing to
 * contest). Either party of the pair may dispatch it — the room reads `actor`'s `Grappled.partner`
 * off its own statuses, same as the break. Resolves `1d20 + actor.hull` vs
 * `1d20 + partner.hull` via `resolveContest` (initiator/dispatcher wins ties);
 * the winner is (re-)stamped with the persistent `Grapple Dominance` marker (`until_removed` —
 * RAW's "until this contest is repeated") and the loser's grapple-caused `Immobilized` is
 * re-synced (`grappleImmobilizedSyncSteps`). No drag yet.
 */
export type RequestGrappleContest = {
  type: "request_grapple_contest";
  /** The Grappled combatant initiating the same-Size tiebreak (the contest's challenger). */
  actor: string;
}

/**
 * **The Grapple-release intent** — the attacker's
 * unconditional **free action** to end a grapple it initiated (Core Book p.72): no roll, clears
 * the `Grappled` marker on both parties. The GM overrides "must be the grapple's attacker" by
 * wrapping this intent in {@link GmForce} (`decisions/0209`), as for any action.
 */
export type RequestGrappleRelease = {
  type: "request_grapple_release";
  /** The attacker ending the grapple it initiated. */
  actor: string;
}

/**
 * **The Disengage intent** — the mech's
 * **Full action** (Core Book p.74): "Until the end of your current turn, you ignore engagement
 * and your movement does not provoke reactions." Wired on the **Hide template** (never a
 * bespoke reducer command, despite the catalog row's `route: "reducer"` display label): the
 * authority lands a first-class `Disengaged` status (`expiry: { kind: "turn", edge: "end", who: "target" }`, so a
 * mid-own-turn self-apply expires at exactly end-of-current-turn via the shipped duration bus —
 * zero new expiry machinery), charged as a Full action (2 points). No legality precondition (no
 * Hide-style cover gate) and no once-per-turn ledger (2 points twice needs 4, over the 3-point
 * ceiling even with Overcharge). Self-target only (you Disengage yourself); idempotent (already
 * Disengaged ⇒ no steps, no re-charge attempted by the room). GM-only override isn't needed —
 * unlike Hide there's no precondition to bypass, so there is no `ForceDisengage` sibling.
 */
export type RequestDisengage = {
  type: "request_disengage";
  /** The mech taking Disengage (self only — you Disengage yourself). */
  actor: string;
}

/**
 * **The Shut Down intent** (Core Book p.71) — the
 * mech's reactor self-shutdown **Quick action**: clear all heat + Exposed, end every
 * tech-caused status, and become the composite **Shut Down** status (Stunned indefinitely +
 * hard tech-immunity) until a {@link RequestBootUp}. Self-target only (you shut
 * your own reactor down); no choices, no legality precondition (unlike Boot Up, RAW attaches
 * none to taking Shut Down), so a single round-trip with no prompt.
 */
export type RequestShutDown = {
  type: "request_shutdown";
  /** The mech taking Shut Down (self only). */
  actor: string;
}

/**
 * **The Boot Up intent** (Core Book p.72) — the
 * mech's reactor restart **Full action**: removes the Shut Down status — that's the whole
 * action. Self-target only; hard-rejected (`checkBootUpLegality`) when the actor isn't Shut
 * Down. Deliberately the ONE action a shut-down mech may still take.
 */
export type RequestBootUp = {
  type: "request_boot_up";
  /** The mech taking Boot Up (self only). */
  actor: string;
}

/**
 * **The Self-Destruct intent** (Core Book pp. 71-72) —
 * the mech's reactor self-destruct **Quick action**: voluntarily arms (or re-arms) the existing
 * reactor-meltdown countdown at `countdown` turns (RAW: "at the end of your next turn, or at the end of
 * one of your turns within the following two rounds (your choice)" — `countdown` ∈ {1,2,3}, chosen at
 * arm). Rides the shipped meltdown machinery (`resolveMeltdownTick`/`resolveMeltdownBlast`): the
 * countdown decrements by 1 at this mech's OWN next `activate_unit` (turn start) and, once it
 * reaches 0, the blast fires at THAT SAME turn's **end** — so `countdown: 1` is RAW-exact
 * "the end of your next turn". Self-target only; no
 * legality precondition beyond `countdown`'s range (always legal to arm/re-arm — RAW has no cancel
 * clause; a GM manually `resource set`-ing the countdown is the fiat cancel backstop).
 * Re-issuing overwrites the prior countdown (re-arming, not stacking).
 */
export type RequestSelfDestruct = {
  type: "request_self_destruct";
  /** The mech arming Self-Destruct (self only). */
  actor: string;
  /** Turns until detonation (1..3), chosen at arm — becomes the `meltdown_countdown` value. The CLI spells it `--at`. */
  countdown: number;
}

/**
 * **The Dismount intent** (`act_dismount`, RAW p.70) — the mech's **Full action**: the
 * pilot climbs out into an adjacent free space, the mech gains `Unpiloted`, and the
 * mech's turn ENDS (the pilot's own turn is a separate, later activation). `actor` is
 * the MECH (dispatched on ITS turn); `at` is the
 * adjacent cell the pilot lands at — rejected (before any dice) if it isn't a free adjacent
 * space. Reaches the generic status-legality wall (unlike Boot Up/Shut Down): pinned by
 * `STUNNED_ACTION_EXCEPTIONS` so a Stunned/Shut-Down mech may still Dismount its pilot (RAW p.78).
 */
export type RequestDismount = {
  type: "request_dismount";
  /** The mech Dismounting its pilot. */
  actor: string;
  /** The adjacent cell the pilot lands at. */
  at: WireCell;
}

/**
 * **The Eject intent** (`act_eject`, RAW p.70) — the mech's **Quick action**: the pilot
 * is flung to any cell within fly 6, the mech gains `Unpiloted` + `Impaired` (until
 * removed) + the Eject lockout (locked out until Full Repair), and the mech's turn ENDS.
 * `actor` is the MECH; `at` is the landing cell (within 6 of the
 * mech, occupancy-checked). Reaches the generic status-legality wall exactly like Dismount —
 * pinned by `STUNNED_ACTION_EXCEPTIONS`.
 */
export type RequestEject = {
  type: "request_eject";
  /** The mech Ejecting its pilot. */
  actor: string;
  /** The landing cell, within fly 6 of the mech. */
  at: WireCell;
}

/**
 * **The Mount intent** (`act_mount`, RAW p.70) — the on-foot
 * pilot's **Full action**: climbing back into its own Unpiloted mech (adjacent), clearing
 * `Unpiloted` and removing the pilot combatant. `actor` is the PILOT (dispatched by the on-foot
 * pilot, never the mech) — deliberately NOT in `STUNNED_ACTION_EXCEPTIONS` (a Stunned MECH can't
 * initiate its own mounting; that exception set gates the mech actor, which Mount never is).
 */
export type RequestMount = {
  type: "request_mount";
  /** The on-foot pilot Mounting. */
  actor: string;
  /** The mech being mounted (must be this pilot's own Unpiloted mech, adjacent). */
  mechId: string;
}

/**
 * **The Jockey intent** (RAW Core p.75) — the on-foot pilot's **Full action** attaching
 * to an adjacent enemy mech: the room resolves
 * `1d20 + actor.hull` (GRIT, per the HASE-as-grit freeze) vs `1d20 + target.hull` via
 * `resolveContest` (initiator/**rider wins ties**). On a rider win-or-tie, the paired
 * `Jockeying`/`Jockeyed` markers land (partner-stamped) and the rider's position is assigned
 * onto the target's cell ("sharing its space") — the `ContestStep` is always recorded
 * (draw-and-record) even on a loss.
 */
export type RequestJockey = {
  type: "request_jockey";
  /** The on-foot pilot attempting to Jockey (the contest's challenger). */
  actor: string;
  /** The adjacent enemy mech being climbed onto (the contest's defender). */
  target: string;
}

/**
 * **The Jockey menu intent** (RAW Core p.75) — a
 * jockeying rider's repeatable **Full action** on each subsequent turn: `actor`'s own
 * `Jockeying` marker names its carrier. `choice` selects one of RAW's three options —
 * `"distract"` (the carrier gains Impaired + Slowed until the end of its next turn),
 * `"shred"` (2 heat to the carrier), or `"damage"` (4 kinetic damage to the carrier, armor
 * applies like any ordinary hit).
 */
export type RequestJockeyMenu = {
  type: "request_jockey_menu";
  /** The jockeying rider (reads its carrier off its own `Jockeying.partner`). */
  actor: string;
  /** Which of RAW's three menu options. */
  choice: "distract" | "shred" | "damage";
  /**
   * Recorded answers to prompts this resolution raised — mirrors {@link RequestAttack.choices}.
   *
   * **`choice` and `choices` are unrelated despite the near-identical names.** `choice` is the
   * rider's menu selection, chosen before dispatch and never changed by a round-trip; `choices` is
   * the generic recorded-answer map every halt-capable intent carries. Do not fold one into the
   * other.
   *
   * The Damage option's cascade can drop the carrier to 0 HP, and a Structure check's System
   * Trauma row assigns the "which mount or system is destroyed" pick to the carrier's own owner
   * (Core p.80, *"of your choice"*) — so this intent halts and round-trips like an attack rather
   * than auto-picking. `projects/silent-equipment-autopick.md` Phase 3 step 9.
   */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **The shake-off intent** (RAW Core p.75) — the
 * Jockeyed carrier's own **Full action** attempting to throw off its rider: the room resolves
 * `1d20 + actor.hull` vs `1d20 + rider.hull` (GRIT) via `resolveContest` (the CARRIER is the
 * challenger here, the mirror of Jockey's own attach contest — the carrier wins ties). On a
 * carrier win-or-tie, both `Jockeying`/`Jockeyed` markers clear; on a loss the ride persists.
 */
export type RequestShakeOff = {
  type: "request_shake_off";
  /** The Jockeyed mech attempting to shake off its rider (reads the rider off its own `Jockeyed.partner`). */
  actor: string;
}

/**
 * **The consent-based allied MOUNT intent** (RAW Core p.70:
 * "you can also MOUNT … willing allied mechs … move into the same space and then move with
 * them") — the on-foot pilot's **Full action** climbing onto an adjacent, SAME-SIDE mech.
 * Structurally the sibling of {@link RequestJockey} (reuses its co-location plumbing),
 * with two differences: the side gate is INVERTED (a same-side match is
 * REQUIRED, the willing-ally reading — vs. Jockey's hostile-mismatch requirement), and there
 * is **no contest** — a willing ally always attaches, no dice, no winner branch. On success,
 * the paired `Riding`/`Ridden` markers land (partner-stamped, a DISTINCT pair from
 * `Jockeying`/`Jockeyed` so the hostile Jockey menu — Distract/Shred/Damage,
 * `request_shake_off` — never lights up for a willing rider) and the rider's position is
 * assigned onto the mech's cell ("sharing its space").
 */
export type RequestRide = {
  type: "request_ride";
  /** The on-foot pilot climbing aboard. */
  actor: string;
  /** The adjacent, willing (SAME-SIDE) allied mech being climbed onto. */
  target: string;
}

/**
 * **The explicit allied-DISMOUNT intent** (RAW Core p.70: "when
 * you DISMOUNT, you are placed in an adjacent space – if there are no free spaces, you cannot
 * DISMOUNT") — the riding rider's own **Full action** climbing back off a willing ally's mech
 * (the mirror trip of {@link RequestRide}). `at` is a PLAYER-CHOSEN adjacent cell, mirroring the
 * mech-bridge {@link RequestDismount}'s own explicit-`at` shape rather than an
 * auto-pick-first-free-neighbor design (`ejectionCellFor`'s pattern, reserved for the destroy
 * consequence's automated spawn, which can't reject — a player-invoked action rejects instead,
 * same as Dismount/Eject): RAW's "you are placed" reads as the rider choosing where, and a
 * rejected `at` gives a precise "no free space at THAT cell" error rather than a silent
 * auto-relocation. On success, the server clears the `Riding`/`Ridden` pair and assigns the
 * rider's position onto `at` via a plain `move` step (never `move_token` — spends no movement
 * budget). Distinct from {@link RequestDismount} (the mech→spawn-pilot bridge verb, a different
 * operation) — this moves an ALREADY-EXISTING on-foot rider off a mech it doesn't itself pilot,
 * never spawning or removing a combatant.
 */
export type RequestRideDismount = {
  type: "request_ride_dismount";
  /** The riding on-foot pilot dismounting (reads its carrier off its own `Riding.partner`). */
  actor: string;
  /** The adjacent free cell the rider lands at (player-chosen — RAW: "placed in an adjacent space"). */
  at: WireCell;
}

/**
 * **The Reload intent** (RAW Core p.75) — the on-foot
 * pilot's own **Quick action**: "you reload one Pilot Weapon with the LOADING tag, making it
 * usable again." Clears the named weapon's `loaded:<instanceKey>` resource back to true absence
 * — the pilot-side sibling of a mech's Stabilize `reload`
 * secondary, but its own single-purpose action (RAW lists it as a distinct pilot verb, not a
 * Stabilize choice). Self-target only (you reload your own weapon). Reaches the SAME generic
 * budget/once-per-turn/status-legality seam Dismount/Eject do (`@lancer-vtt/rules`'
 * `economy.ts`'s `actionIdForIntent`/`checkActionUseLegality`) — RAW's own "characters can't
 * take the same action more than once per turn" applies to it like any other core verb.
 */
export type RequestReload = {
  type: "request_reload";
  /** The reloading pilot (self only — you reload your own weapon). */
  actor: string;
  /** Optional weapon **instance key** ({@link WeaponProfile.instanceId}/`weaponInstanceKey`) naming which Loading weapon to reload. Absent ⇒ the actor's FIRST Loading weapon. */
  weapon?: string;
}

/**
 * The v1-restricted damage type a mine's detonation payload may carry — the wire-side
 * mirror of `@lancer-vtt/domain`'s `DamageType`, narrowed to the
 * three types {@link "@lancer-vtt/rules".spawnMine} accepts (`kinetic`/`energy`/`explosive` —
 * the ownerless burst reuses the simple mitigate-then-cascade shape, not the full weapon
 * damage-type router `burn`/`heat`/`variable` need). Declared here (not imported) because
 * `shared/` is leaf and cannot import `domain`/`rules`.
 */
export type MineDamageType = "kinetic" | "energy" | "explosive";

/**
 * **The Deploy Drone intent** (Core Book p.105 — DRONE tag) — the owner's **Quick
 * action**: mint a fresh SIZE-½ drone combatant, controlled by the
 * SAME slot that controls `actor` (derived server-side from `actor`'s own frozen
 * `controllerSlotId`, never a client-supplied field, so a player can't spoof another slot's
 * ownership onto the spawned drone), placed at `at` — hard-gated (before any dice/spend) to
 * an **adjacent (Range 1) FREE** cell (occupied/terrain-solid rejects), mirroring
 * `RequestDismount`'s adjacency shape. `mintedId` is client-minted (like `add_annotation`'s
 * `id`) so the caller can address the drone in the SAME reply without a server round-trip.
 */
export type RequestDeployDrone = {
  type: "request_deploy_drone";
  /** The mech/pilot spending its own Quick action to deploy (the drone's controller derives from THIS combatant). */
  actor: string;
  /** The client-minted id for the spawned drone — an id being MINTED, never an acting combatant (the actor is `actor`). */
  mintedId: string;
  /** The drone's display name. */
  name: string;
  /** The adjacent free cell it deploys to. */
  at: WireCell;
}

/**
 * **The Deploy Mine intent** (Core Book p.106 — MINE tag) —
 * the owner's **Quick action**: mint a fresh hidden (`concealed: true`) mine combatant at `at`
 * — hard-gated to an adjacent (Range 1) FREE cell **NOT adjacent to another mine** (Core p.106:
 * "not adjacent to any other mines") — `mineProfile.deployedBy` is stamped server-side to
 * `actor` (the arm-timing pointer — the turn-end sweep arms it when THIS combatant's turn
 * ends) and `controllerSlotId` derives from `actor`'s own controller ("deployer + GM see
 * the mine"), mirroring `RequestDeployDrone`'s ownership-derivation discipline.
 *
 * **Two data sources for the detonation payload:**
 *   - **`gearId` set** — the item-derived path. The authority DERIVES `burstRadius`/`damage`/
 *     `damageType`/`saveAxis` (and computes its own `saveDc` off the deployer's frozen
 *     `saveTarget`) from that pilot-gear id's own `deployables[0]` via `gearDeployableMineProfile`
 *     — server-authoritative; any `burstRadius`/`damage`/`damageType`/`saveAxis`/`saveDc` also
 *     present on the wire are IGNORED, not trusted. This is a data-SOURCE choice (item-derived vs
 *     caller-specified), not a rules-softening flag — both paths run the identical budget/
 *     placement/adjacency/ownership walls.
 *   - **`gearId` absent** — the original caller-specified path (the generic GM/testing deploy,
 *     also the CLI's `add --mine` authored shape): `burstRadius`/`damage`/`damageType` are
 *     required, `saveAxis` requires `saveDc`; both omitted auto-hits (no save, Core p.106 default).
 */
export type RequestDeployMine = {
  type: "request_deploy_mine";
  /** The mech/pilot spending its own Quick action to deploy (the mine's `deployedBy`/controller derive from THIS combatant). */
  actor: string;
  /** The client-minted id for the spawned mine — an id being MINTED, never an acting combatant (the actor is `actor`). */
  mintedId: string;
  /** The mine's display name. */
  name: string;
  /** The adjacent free, non-mine-adjacent cell it deploys to. */
  at: WireCell;
  /** A pilot-gear compendium id whose OWN `deployables[0]` data the authority derives the detonation payload from (e.g. `pg_thermite_charge`) — when set, `burstRadius`/`damage`/`damageType`/`saveAxis`/`saveDc` below are ignored. */
  gearId?: string;
  /** The BURST radius the detonation covers. Required (and authoritative) only when `gearId` is absent. */
  burstRadius?: number;
  /** Pre-mitigation damage dealt to each hit character. Required (and authoritative) only when `gearId` is absent. */
  damage?: number;
  /** The detonation's damage type (v1-restricted — see {@link MineDamageType}). Required (and authoritative) only when `gearId` is absent. */
  damageType?: MineDamageType;
  /** The forced-save axis label; omit (with `saveDc`) for auto-hit. Read only when `gearId` is absent. */
  saveAxis?: string;
  /** The save DC — required when `saveAxis` is set. Read only when `gearId` is absent. */
  saveDc?: number;
}

/**
 * **The Detect Mine intent** (Core Book p.106) — the searcher's **Quick action**: an
 * AREA-shaped SYSTEMS-vs-DC sweep within `actor`'s Sensors, carrying **no mine id** (the
 * client, by construction, cannot name a concealed mine it doesn't have in its own
 * projected state — the whole point of the redaction). The
 * authority resolves against **full authoritative state** and reveals whatever concealed
 * mine qualifies (within Sensors range) on a pass, via a `concealed`-clearing gameplay reveal
 * step. **THE ORACLE CONSTRAINT (load-bearing):** a FAILED sweep's player-visible emitted
 * events are BYTE-IDENTICAL whether or not a hidden mine exists anywhere in range — the
 * resolver never reads mine state until AFTER the roll succeeds, so a miss never leaks
 * presence/absence through the shape of what it emits.
 */
export type RequestDetectMine = {
  type: "request_detect_mine";
  /** The searcher spending its own Quick action to sweep. */
  actor: string;
}

/**
 * **The Disarm Mine intent** (Core Book p.106) — the disarmer's **Quick action**,
 * entity-targeted (legitimate ONLY because a revealed-or-owned mine is the one thing the
 * disarmer's OWN projected state could ever name — the redaction does the gating, not
 * this intent): adjacent (Range 1) to `target`, a SYSTEMS-vs-DC check.
 * Success removes the mine cleanly; **failure detonates it** (Core p.106: "on a failure, the
 * mine detonates as normal") — the IDENTICAL `resolveMineDetonation` burst, centered
 * on the mine, catching the disarmer.
 */
export type RequestDisarmMine = {
  type: "request_disarm_mine";
  /** The disarmer spending its own Quick action (must be adjacent to `target`). */
  actor: string;
  /** The mine being disarmed. */
  target: string;
  /**
   * Recorded answers to prompts this resolution raised — mirrors {@link RequestAttack.choices}. A
   * failed disarm's burst can drop a caught mech to 0 HP, and a Structure check's System Trauma row
   * gives the "which system is destroyed" pick to that mech's owner (Core p.80), so this intent
   * halts and round-trips like a proximity trigger's `request_move` rather than auto-picking.
   */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **The Detonate Mine intent** (MINE tag, Core Book p.106) — a **remote-only** manual
 * detonation, distinct from the default proximity
 * arm/trigger sweep `RequestDeployMine`'s mines ride: some MINE-tagged items (Thermite
 * Charge: "This charge must be remotely detonated as a quick action") never arm/trigger on
 * proximity at all and are ONLY ever set off by their own deployer, on demand, as a Quick
 * action. `target` must be a mine `actor` itself deployed (`mineProfile.deployedBy ===
 * actor`, server-verified — never trust a wire-claimed ownership); no adjacency/range
 * requirement (RAW: "remotely"). Reuses the SAME `resolveMineDetonation` burst the proximity
 * path fires — same reveal/damage/save/cascade shape, same `remove_combatant` spend-the-mine
 * follow-up — only the trigger condition (deployer's own choice vs. an enemy's move) differs.
 */
export type RequestDetonateMine = {
  type: "request_detonate_mine";
  /** The deployer spending its own Quick action to remotely detonate its own placed mine. */
  actor: string;
  /** The mine being detonated (must be `actor`'s own — `mineProfile.deployedBy`). */
  target: string;
  /**
   * Recorded answers to prompts the burst raised — mirrors {@link RequestDisarmMine.choices}: a
   * caught character's Bondmate may Intercede, and a caught mech's System Trauma pick goes to its owner.
   */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **The Engage COMP/CON intent** — a **protocol** (0 action points) that hands a
 * mech's turn over to its own COMP/CON. Reachable ONLY when the actor carries a
 * cascade-immune AI system — an equipped SYSTEM tagged BOTH `tg_ai` AND `tg_no_cascade`
 * (re-derived at command time, never a frozen flag: it gates off the shared
 * cascade-immunity fact rather than a dedicated capability tag, which upstream data does
 * not carry; see `@lancer-vtt/rules`' `economy.ts`'s `hasCompConSystem`); rejected with
 * no comp/con carrier, and rejected (idempotent no-op) if already engaged. Lands the
 * `"COMP/CON Engaged"` status (`COMPCON_ENGAGED_STATUS`), under which the pilot's talents and
 * core bonuses stop working (Core p.106). No extra fields — self-target only, like
 * `RequestShutDown`.
 */
export type RequestEngageCompCon = {
  type: "request_engage_compcon";
  /** The mech engaging its own COMP/CON (self only). */
  actor: string;
}

/**
 * **Take control back from COMP/CON** — a protocol (Core p.106: "At the start of the player's
 * subsequent turns, control can be returned to the pilot as long as they are physically inside
 * their mech"). Legal only at the start of the mech's own turn, on a later turn than the Engage,
 * while its pilot is aboard. Removes `"COMP/CON Engaged"`.
 */
export type RequestTakeBackCompCon = {
  type: "request_take_back_compcon";
  /** The mech whose pilot takes control back (self only). */
  actor: string;
}

/**
 * **The move intent** — the PLAY-mode
 * voluntary-movement counterpart to the `move_token` command (two-command-tiers doctrine:
 * the force-apply `place_token`/`teleport_token`/`forced_move` steps stay plain commands
 * and never ride this — involuntary/GM movement never halts). The room resolves it through
 * a checkpoint pre-apply pass (mirroring `resolve()`'s shape) before emitting the
 * **unchanged** `move_token` command via the **unchanged** `applyMove`. A checkpoint
 * gather may halt this for a reaction round-trip (echoed back on `encounter_prompt`,
 * answered by re-submitting this same intent) before that emission; with no consumer
 * registered it resolves immediately, byte-identical to a direct `move_token` dispatch.
 * Field names deliberately mirror
 * `MoveTokenCommand`'s (`combatantId`/`to`), not the `actor`/`at` shape other movement
 * intents use, since this is the direct wire twin of that command.
 *
 * **`choices`/`recordedDice`**: the checkpoint-gather round-trip pair, needed the moment a
 * checkpoint consumer can halt this intent — identical shape/contract to every other
 * `EncounterIntent`'s own `choices`/`recordedDice`
 * (`RequestAttack`'s doc has the full round-trip explanation). A watcher's checkpoint answer
 * (decline, or a recorded "take" after its own sibling attack resolves) records into `choices`
 * keyed by the offer's own prompt id; the room's `encounter_prompt` echoes this intent back with
 * `recordedDice` riding the dice-so-far, so a re-submit replays deterministically. Both
 * absent on a first submit — byte-identical when nothing halts.
 */
export type RequestMove = {
  type: "request_move";
  /** The combatant declaring movement (the mover). */
  combatantId: string;
  /** The declared destination anchor cell. */
  to: WireCell;
  /**
   * The chosen route, as anchor cells from the mover's cell to `to` (`landing-zone-full-automation`
   * Q1): the Trampler's "through this character". Rides onto the `move_token`, which checks and
   * charges it. Absent, the move takes the engine's route.
   */
  path?: WireCell[];
  /** Recorded checkpoint-offer answers, keyed by prompt id (mirrors {@link RequestAttack.choices}). */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a checkpoint prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
  /**
   * `false` refuses the Boost fold (`move --no-boost`). Absent, when only a Boost reaches `to`
   * (`move-auto-boost`) the room folds the `boost` command ahead of the move, as one undo step,
   * or refuses the move naming why the Boost is refused. With `false`, a move past the move left
   * is rejected.
   */
  autoBoost?: false;
}

/**
 * **The end-turn intent** — the PLAY-mode counterpart to the `end_turn` command, riding the
 * SAME two-command-tiers seam `RequestMove` opened for `move_token`
 * (`decisions/0164` Decision 2): burn and reactor meltdown are reachable ONLY from `end_turn`'s
 * hook chain (`driveEndTurn`), and once either can raise the System Trauma "of your choice"
 * destruction pick (Core p.80), that chain needs an answer channel a bare `EncounterCommand`
 * cannot carry (no `choices`, no `recordedDice`). Rather than widen the prompt echo to accept a
 * command — which would teach `sendDecisionPrompt`/`resolutionSource`/`resolutionParticipants`/
 * `withRecordedChoice` a second echo shape for one caller — this intent gives the chain the
 * SAME wire shape every other halt-capable resolution already uses.
 *
 * The room routes this off `handle`'s `encounter_intent` case BEFORE the generic
 * `resolveIntent`/`resolveIntentAfterReveal` ladder, exactly like `RequestMove`, to a dedicated
 * referee (`refereeEndTurn`) that synthesizes the unchanged `end_turn` command and drives the
 * SAME `driveEndTurn` chain `refereeCommand`'s raw command tier drives — interactively. The raw
 * `end_turn` COMMAND (CLI/AI/tests) keeps auto-picking; `../tables.ts` names that the permanent
 * fallback leg, not a scope cut.
 *
 * **`choices`/`recordedDice`**: the same halt/replay round-trip pair every other halt-capable
 * intent carries (`RequestMove`'s own doc has the full round-trip explanation; `RequestAttack`'s
 * carries the original one). Both absent on a first submit — byte-identical to a direct
 * `end_turn` dispatch when nothing in the chain halts.
 */
export type RequestEndTurn = {
  type: "request_end_turn";
  /** The combatant ending its turn (mirrors `EndTurnCommand.combatantId`). */
  combatantId: string;
  /** Recorded prompt answers, keyed by prompt id (mirrors {@link RequestMove.choices}). */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a re-resolve (mirrors {@link RequestMove.recordedDice}). */
  recordedDice?: number[];
}

/**
 * The resolution-intent union — **open by design**: a new intent slots in without
 * reshaping the wire. `RequestAttack` is the weapon-attack spine and `RequestActivate`
 * the overlay-`action` sibling; `GmForce` is the GM override wrapper;
 * `RequestBarrage` one shot of a sequential Barrage; `RequestStabilize` the mech
 * self-maintenance Full action; `RequestOvercharge` the reactor push; `RequestFullTech`
 * one pick of a Full Tech; `RequestHide` the stealth Quick action; `RequestSearch` the stealth-countering Quick action; `RequestGrappleBreak` the
 * Grapple defender's contested-HULL break, `RequestGrappleRelease` the attacker's release, `RequestGrappleContest` the same-Size turn-start tiebreak;
 * `RequestDisengage` the movement-immunity Full action; `RequestShutDown`/`RequestBootUp`
 * the reactor self-shutdown/restart pair and `RequestSelfDestruct` the meltdown arm;
 * `RequestDismount`/`RequestEject` the mech's pilot-ejection pair and `RequestMount` the
 * pilot's return trip; `RequestJockey` the on-foot pilot's rider-attach contest,
 * `RequestJockeyMenu` its repeatable Distract/Shred/Damage follow-up, and
 * `RequestShakeOff` the carrier's throw-off contest; `RequestRide` the consent-based
 * allied MOUNT — Jockey's no-contest, same-side-required sibling; `RequestReload` the
 * on-foot pilot's Loading-weapon reload; `RequestDeployDrone`/`RequestDeployMine` the
 * economy-gated owner deploys and `RequestDetectMine`/`RequestDisarmMine` the Sensors
 * sweep + adjacent disarm; `RequestDetonateMine` the deployer's remote-only manual
 * detonation, for MINE items that never use the proximity arm/trigger sweep;
 * `RequestEngageCompCon` the protocol handoff to a mech's own COMP/CON, reachable only
 * with a cascade-immune AI system carried (`tg_ai` + `tg_no_cascade`), and
 * `RequestTakeBackCompCon` the pilot's protocol taking it back. `RequestMove` is the PLAY-mode voluntary movement counterpart to
 * `move_token`, riding the checkpoint pre-apply seam; `RequestEndTurn` is the identical
 * counterpart to `end_turn`, opened so burn/meltdown can prompt (`decisions/0164`).
 */
/**
 * The damage types a GM may adjudicate — the wire-side mirror of `@lancer-vtt/domain`'s
 * `DamageType` minus `"variable"`. Declared here rather than imported for the same reason
 * {@link MineDamageType} is: `shared/` is leaf and cannot import `domain`/`rules`.
 *
 * **`"variable"` is excluded deliberately, not narrowed for convenience** (unlike
 * {@link MineDamageType}, which is a real v1 scope cut): it is a weapon-authoring placeholder that
 * must resolve to a concrete type before damage is ever routed, so it has no meaning for a GM
 * typing a number. Every other member of `DamageType` is offered.
 *
 * Because this is a hand-written mirror it can drift from the real union, so it is **pinned**:
 * `packages/rules/src/encounter/referee/gmDamageType.pin.test.ts` fails compilation if this type
 * and `Exclude<DamageType, "variable">` stop being mutually assignable.
 */
export type GmDamageType = "kinetic" | "energy" | "explosive" | "burn" | "heat";

/**
 * **The GM-adjudicated damage intent** (`gm-adjudicated-damage` Phase 1) — the GM applies
 * **N damage of a chosen type** to one combatant, running the SAME damage path an attack runs.
 *
 * **Why this exists as a first-class intent rather than a GM writing HP directly.** Setting HP by
 * hand skips every rule that makes damage mean something: armor, AP, Resistance, the Overshield
 * drain, the Burn mark, the Heat→Overheating cascade, the Structure cascade, and — the reason the
 * developer asked for it — destruction. `resolveDamageCascade` never leaves a structure-tracked
 * combatant resting at 0 HP (it destroys it, or resets HP to full in the same fold), so routing
 * GM adjudication through damage makes the 0-HP state **unreachable** rather than a state the
 * engine has to interpret. Out-of-band damage the GM has adjudicated at the table (a fall, a hazard
 * we have not modelled, a ruling) therefore lands with real consequences instead of as bookkeeping.
 *
 * **There is no attacker, and that is the defining difference from every other resolution intent.**
 * The GM is the source, not a combatant — so this intent names only its `target`, carries no
 * `actor`, and is **GM-only** (`canResolveIntent`): the ownership doctrine gates on a command's
 * initiating combatant, and with none to key on, player tier has nothing to check. It also routes
 * PRE-ladder, so GM fiat collects no budget, once-per-turn or actor-status gates.
 *
 * **`amount` is PRE-mitigation** — the number the GM says was dealt, before armor and Resistance,
 * exactly like a weapon's rolled damage. Armor then applies unless `ap` is set or the type ignores
 * it (burn/heat always do), so a GM typing "10 kinetic" at an Armor-2 target lands 8.
 */
export type RequestGmDamage = {
  type: "request_gm_damage";
  /** The combatant taking the damage. There is no attacker — see the type doc. */
  target: string;
  /** Pre-mitigation damage (a non-negative integer) — armor/Resistance apply after, per RAW. */
  amount: number;
  /**
   * The damage type — see {@link GmDamageType} for why `"variable"` is not offered. */
  damageType: GmDamageType;
  /** Armor-piercing — the RAW flag, not a type. Inert for burn/heat, which ignore armor anyway. */
  ap?: boolean;
  /** Recorded answers to prompts this resolution raised — mirrors {@link RequestAttack.choices}. */
  choices?: Record<string, string>;
  /** Recorded dice — the monotonic input log replayed on a prompt re-resolve (mirrors {@link RequestAttack.recordedDice}). */
  recordedDice?: number[];
}

/**
 * **`startTurn`: "if this unit is ready, this action starts its turn"** (`token-command-menu` 2c,
 * option B). Honoured for the GM only: on any ready unit, a PC's included, the room then folds
 * `activate_unit` in front of the GM's action or move exactly as it does for a player's own unit
 * (`controlsActivation`, `backend/src/encounterActivationFold.ts`). The browser sets it on every GM intent; the CLI never
 * does, so the CLI, the CLI proofs and GM adjudication keep the wave-through. It rides on the intent rather than the envelope because the room re-derives the fold
 * from the intent at every point it reads it, and a decision answer re-submits the echoed intent.
 */
export type IntentTurnFlag = { startTurn?: true };

/**
 * Every resolution intent a client may send. **Field vocabulary across members** — a generic reader
 * reads fields by name, so a new verb should reuse a name only with its meaning below:
 *   - `actor` / `attacker` — the acting combatant (`resolutionSource` says which per member).
 *   - `combatantId` — the acting combatant, on `request_move` / `request_end_turn` only (the wire
 *     twins of `move_token` / `end_turn`, `decisions/0164`). An id being minted is `mintedId`.
 *   - `at` — a cell something is placed at; `to` — a cell the subject moves to. A count of turns is
 *     `countdown`, never `at`.
 *   - `target` — still carries three meanings, deliberately left as-is: an `AttackTarget` on the
 *     attack family, a unit id on most others, and a mine id on the two mine verbs (the damage
 *     recipient on `request_gm_damage`). Narrow on `type` before reading it.
 *   - `damageType` — `MineDamageType` on `request_deploy_mine` is a subset of `GmDamageType`.
 */
export type EncounterIntent = (RequestAttack | RequestActivate | GmForce | RequestBarrage | EndAttack | StopBarrageAsSkirmish | RequestStabilize | RequestOvercharge | RequestFullTech | RequestHide | RequestSearch | RequestGrappleBreak | RequestGrappleRelease | RequestGrappleContest | RequestDisengage | RequestShutDown | RequestBootUp | RequestSelfDestruct | RequestDismount | RequestEject | RequestMount | RequestJockey | RequestJockeyMenu | RequestShakeOff | RequestRide | RequestRideDismount | RequestReload | RequestDeployDrone | RequestDeployMine | RequestDetectMine | RequestDisarmMine | RequestDetonateMine | RequestEngageCompCon | RequestTakeBackCompCon | RequestGmDamage | RequestMove | RequestEndTurn) & IntentTurnFlag;

/**
 * The **source / initiator** combatant of a resolution intent — the attacker (attack /
 * barrage / end-attack; a `gm_force` answers for the intent it wraps), the mover (`request_move`), or the actor (every
 * other activation-shaped intent). Pure over the wire `EncounterIntent` union, so it is the
 * single source of truth for "who is acting" on **both** ends: the authority uses it to route
 * a decision prompt (same-actor → the acting socket only, no COMMS card; cross-actor → the
 * decider + GM, with a card — `backend/src/encounterRoom.ts`'s `sendDecisionPrompt`), and the
 * client uses it to tell whether a received prompt is its own same-actor choice (which never
 * gets a card, so the floating window must show) or a cross-actor backstop it may defer to the
 * card (`frontend/src/routes/home/CombatApp.tsx`'s `suppressPromptModalForGmBystander`).
 */
export function resolutionSource(intent: EncounterIntent): string {
  switch (intent.type) {
    case "gm_force":
      return resolutionSource(intent.intent);
    case "request_attack":
    case "request_barrage":
    case "end_attack":
    case "stop_barrage_as_skirmish":
      return intent.attacker;
    case "request_move":
    case "request_end_turn":
      return intent.combatantId;
    // **GM damage has no source combatant at all** — the GM is the initiator, and the GM is not a
    // unit on the board. `""` is the honest answer (no combatant can hold the empty id), and it is
    // load-bearing rather than a placeholder in two places:
    //
    //   - `canResolveIntent` already documents "an intent with no identifiable source is GM-only",
    //     which is exactly this case (it is denied earlier, by name, so the deny is by rule).
    //   - `sendDecisionPrompt` short-circuits a prompt to the ACTING SOCKET ALONE when
    //     `decision.who === resolutionSource(intent)` — the "you are answering your own choice, no
    //     COMMS card needed" path. Returning the TARGET here (the first thing tried, and it looked
    //     reasonable) made `who === source` and silently routed a player's own System Trauma
    //     choice to the GM only. `""` can never equal a decider id, so the prompt always takes the
    //     cross-actor branch and reaches the target's owner — which is the whole of D3.
    case "request_gm_damage":
      return "";
    default:
      return intent.actor;
  }
}

/**
 * Every {@link EncounterIntent} member that carries a `choices` map (a recorded prompt answer),
 * derived from the union rather than hand-listed: a shape that gains `choices` widens this the
 * moment the wire type changes, and one that drops it narrows it.
 */
export type ChoiceCarryingIntent = EncounterIntent extends infer T ? (T extends EncounterIntent ? ("choices" extends keyof T ? T : never) : never) : never;

/**
 * Runtime twin of {@link ChoiceCarryingIntent}, pinned BOTH ways by tsc: `Record<K, true>` from an
 * object literal requires every key of `K` (a missing shape is a compile error) and rejects an
 * excess key (a shape without `choices` is a compile error too). Shared by the CLI drive loop's
 * loud `mergeChoice` and `@lancer-vtt/ai`'s silent `withRecordedChoice`; the two differ in what
 * they do with an unmergeable intent, which lives in those consumers, not in this set.
 */
export const CHOICE_CARRYING_INTENT_TYPES: Record<ChoiceCarryingIntent["type"], true> = {
  request_attack: true,
  request_activate: true,
  request_barrage: true,
  request_full_tech: true,
  request_move: true,
  // GM damage's cascade runs `interactive: true` — a Structure check's System Trauma row raises a
  // real "which mount or system" prompt, answered and re-submitted like a variable-damage-type
  // prompt on an attack. The first non-attack verb in this set.
  request_gm_damage: true,
  // Overcharge: Reactor Stabilizer's keep-or-reroll on the Overheating roll.
  request_overcharge: true,
  // Jockey Menu (`silent-equipment-autopick` Phase 3): its Damage option runs 4 kinetic into the
  // carrier, and a carrier that reaches 0 HP raises the carrier owner's System Trauma pick.
  request_jockey_menu: true,
  // Disarm Mine (`mine-detect-disarm` 2.2): a failed check detonates the mine, and a caught mech
  // that loses a structure point raises its owner's System Trauma pick.
  request_disarm_mine: true,
  // Detonate Mine (`ll1-granted-reactions` 5.2): the same burst, so the same picks, plus Intercede.
  request_detonate_mine: true,
  // `request_end_turn` (`silent-equipment-autopick` Phase 4): burn and reactor meltdown can raise the
  // System Trauma pick from `end_turn`'s own hook chain, the same pre-ladder relocation
  // `request_move` got.
  request_end_turn: true,
};

/**
 * The `reactionToward`-carrying subset of {@link EncounterIntent} — the answer-by-direction
 * counterpart of {@link ChoiceCarryingIntent}, same derivation.
 */
export type ReactionTowardIntent = EncounterIntent extends infer T ? (T extends EncounterIntent ? ("reactionToward" extends keyof T ? T : never) : never) : never;
